/**
 * Brand campaign draft (BR-04 / FR-CAM-001..005).
 *
 * The wizard is seven steps because a single stacked card hides the
 * missing brief, the date order and the cap math. Validation and the
 * PATCH body live here so the screen cannot drift from what the server
 * will accept, and so the tests do not have to mount the form.
 */

export const WIZARD_STEPS = [
  { id: "basics", title: "Basics", hint: "What creators see first in discovery." },
  { id: "brief", title: "Brief & rights", hint: "The page a creator reads before they film." },
  { id: "assets", title: "Assets", hint: "A 3:4 cover photo for discovery, plus licensed source files scanned before anyone can download them." },
  { id: "creators", title: "Creators", hint: "Who can take part. Anyone can join." },
  { id: "rewards", title: "Rewards", hint: "The rate is held from your budget the moment a creator is accepted." },
  { id: "schedule", title: "Schedule", hint: "When creators can join, and how long posts stay live." },
  { id: "preview", title: "Preview", hint: "Check the brief the way a creator will see it, then send it for review." },
] as const;

export type WizardStepId = (typeof WIZARD_STEPS)[number]["id"];

export const BRIEF_FIELDS: {
  key: string; label: string; hint: string; rows: number; required: boolean;
  kind?: "platforms";
}[] = [
  { key: "deliverables", label: "Deliverables", rows: 3, required: true,
    hint: "What they post, how many, and on which account." },
  { key: "creative_directions", label: "Creative direction", rows: 4, required: true,
    hint: "Tone, references, things to show. Creators read this before they film." },
  { key: "hashtags", label: "Required hashtags and mentions", rows: 2, required: true,
    hint: "Exactly as they should appear, including #ad." },
  { key: "prohibited", label: "Not allowed", rows: 3, required: false,
    hint: "Competitor products, claims you cannot substantiate." },
  { key: "allowed_providers", label: "Required platforms", rows: 2, required: true,
    kind: "platforms",
    hint: "The accounts they may post from. Do not promise a view count." },
  { key: "posting_requirements", label: "Posting requirements", rows: 2, required: true,
    hint: "How long the post must stay live, and any pinning or disclosure." },
  { key: "usage_rights", label: "Usage rights", rows: 3, required: true,
    hint: "What you may do with the content, and for how long." },
];

export type DraftPlatformGroup = "social" | "forum";

export interface DraftPlatform {
  id: string;
  label: string;
  group: DraftPlatformGroup;
  aliases?: readonly string[];
}

export const DRAFT_PLATFORMS: readonly DraftPlatform[] = [
  { id: "youtube", label: "YouTube", group: "social", aliases: ["yt"] },
  { id: "tiktok", label: "TikTok", group: "social", aliases: ["tt"] },
  { id: "instagram", label: "Instagram", group: "social", aliases: ["ig", "insta"] },
  { id: "facebook", label: "Facebook", group: "social", aliases: ["fb"] },
  { id: "x", label: "X", group: "social", aliases: ["twitter", "x.com"] },
  { id: "snapchat", label: "Snapchat", group: "social", aliases: ["snap"] },
  { id: "linkedin", label: "LinkedIn", group: "social", aliases: ["li"] },
  { id: "pinterest", label: "Pinterest", group: "social" },
  { id: "threads", label: "Threads", group: "social" },
  { id: "reddit", label: "Reddit", group: "forum" },
  { id: "twitch", label: "Twitch", group: "social" },
  { id: "kick", label: "Kick", group: "social" },
  { id: "discord", label: "Discord", group: "forum" },
  { id: "whatsapp", label: "WhatsApp", group: "social", aliases: ["wa"] },
  { id: "telegram", label: "Telegram", group: "social" },
  { id: "wechat", label: "WeChat", group: "social" },
  { id: "messenger", label: "Messenger", group: "social" },
  { id: "line", label: "LINE", group: "social" },
  { id: "kakaotalk", label: "KakaoTalk", group: "social", aliases: ["kakao"] },
  { id: "viber", label: "Viber", group: "social" },
  { id: "signal", label: "Signal", group: "social" },
  { id: "tumblr", label: "Tumblr", group: "social" },
  { id: "bluesky", label: "Bluesky", group: "social", aliases: ["bsky"] },
  { id: "mastodon", label: "Mastodon", group: "social" },
  { id: "vk", label: "VK", group: "social", aliases: ["vkontakte"] },
  { id: "weibo", label: "Weibo", group: "social" },
  { id: "xiaohongshu", label: "Xiaohongshu", group: "social", aliases: ["rednote", "xhs", "little red book"] },
  { id: "douyin", label: "Douyin", group: "social" },
  { id: "kwai", label: "Kwai", group: "social" },
  { id: "likee", label: "Likee", group: "social" },
  { id: "lemon8", label: "Lemon8", group: "social" },
  { id: "bereal", label: "BeReal", group: "social" },
  { id: "nextdoor", label: "Nextdoor", group: "social" },
  { id: "clubhouse", label: "Clubhouse", group: "social" },
  { id: "truthsocial", label: "Truth Social", group: "social" },
  { id: "vimeo", label: "Vimeo", group: "social" },
  { id: "rumble", label: "Rumble", group: "social" },
  { id: "dailymotion", label: "Dailymotion", group: "social" },
  { id: "soundcloud", label: "SoundCloud", group: "social" },
  { id: "spotify", label: "Spotify", group: "social" },
  { id: "bandcamp", label: "Bandcamp", group: "social" },
  { id: "medium", label: "Medium", group: "forum" },
  { id: "substack", label: "Substack", group: "forum" },
  { id: "patreon", label: "Patreon", group: "social" },
  { id: "quora", label: "Quora", group: "forum" },
  { id: "stackoverflow", label: "Stack Overflow", group: "forum", aliases: ["so"] },
  { id: "hackernews", label: "Hacker News", group: "forum", aliases: ["hn", "ycombinator"] },
  { id: "producthunt", label: "Product Hunt", group: "forum", aliases: ["ph"] },
  { id: "indiehackers", label: "Indie Hackers", group: "forum" },
  { id: "discourse", label: "Discourse", group: "forum" },
  { id: "github", label: "GitHub", group: "forum" },
  { id: "fandom", label: "Fandom", group: "forum", aliases: ["wikia"] },
  { id: "steam", label: "Steam", group: "forum" },
  { id: "deviantart", label: "DeviantArt", group: "forum" },
  { id: "behance", label: "Behance", group: "social" },
  { id: "dribbble", label: "Dribbble", group: "social" },
  { id: "flickr", label: "Flickr", group: "social" },
  { id: "imgur", label: "Imgur", group: "forum" },
  { id: "9gag", label: "9GAG", group: "forum" },
  { id: "letterboxd", label: "Letterboxd", group: "social" },
  { id: "goodreads", label: "Goodreads", group: "forum" },
  { id: "wattpad", label: "Wattpad", group: "forum" },
  { id: "ao3", label: "Archive of Our Own", group: "forum", aliases: ["ao3"] },
  { id: "blind", label: "Blind", group: "forum" },
  { id: "glassdoor", label: "Glassdoor", group: "forum" },
  { id: "tripadvisor", label: "Tripadvisor", group: "forum" },
  { id: "yelp", label: "Yelp", group: "forum" },
  { id: "meetup", label: "Meetup", group: "social" },
  { id: "zhihu", label: "Zhihu", group: "forum" },
  { id: "douban", label: "Douban", group: "forum" },
  { id: "tieba", label: "Baidu Tieba", group: "forum", aliases: ["tieba"] },
  { id: "xing", label: "Xing", group: "social" },
  { id: "okru", label: "Odnoklassniki", group: "social", aliases: ["ok.ru"] },
  { id: "slack", label: "Slack", group: "forum" },
  { id: "skool", label: "Skool", group: "forum" },
  { id: "circle", label: "Circle", group: "forum" },
];

const PLATFORM_BY_KEY: Record<string, string> = {};
for (const p of DRAFT_PLATFORMS) {
  PLATFORM_BY_KEY[p.id] = p.id;
  PLATFORM_BY_KEY[p.label.toLowerCase()] = p.id;
  PLATFORM_BY_KEY[p.label.toLowerCase().replace(/[^a-z0-9]+/g, "")] = p.id;
  for (const alias of p.aliases ?? []) {
    PLATFORM_BY_KEY[alias.toLowerCase()] = p.id;
    PLATFORM_BY_KEY[alias.toLowerCase().replace(/[^a-z0-9]+/g, "")] = p.id;
  }
}

export function parseDraftPlatforms(raw: string): string[] {
  const ids: string[] = [];
  for (const part of raw.split(/[,;/&+]|\band\b/i)) {
    const key = part.trim().toLowerCase();
    if (!key) continue;
    const id = PLATFORM_BY_KEY[key] ?? PLATFORM_BY_KEY[key.replace(/[^a-z0-9]+/g, "")];
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

export function formatDraftPlatforms(ids: readonly string[]): string {
  return DRAFT_PLATFORMS.filter((p) => ids.includes(p.id)).map((p) => p.label).join(", ");
}

export function filterDraftPlatforms(query: string): DraftPlatform[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...DRAFT_PLATFORMS];
  return DRAFT_PLATFORMS.filter((p) =>
    p.label.toLowerCase().includes(q)
    || p.id.includes(q)
    || (p.aliases ?? []).some((a) => a.toLowerCase().includes(q)));
}

export const TIMEZONES = [
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
] as const;

export const MODE_LABEL: Record<string, string> = {
  OPEN_JOIN: "Open to join - they accept the terms and start",
  APPLICATION: "By application - you pick who is offered",
  INVITE_ONLY: "Invite only - nobody else can see it",
};

export const TYPE_LABEL: Record<string, string> = {
  ORIGINAL_UGC: "Original UGC - they film new work",
  AUTHORIZED_CLIPPING: "Clipping - they cut from licensed source",
};

/** The smallest campaign pool a brand can send for review, in cents. */
export const MIN_CAMPAIGN_BUDGET_MINOR = 10_000;

export interface PlatformTerm {
  platform: string;
  budget_minor: number;
  cpm_gross_minor: number;
}

export interface DraftFormState {
  display_name: string;
  summary: string;
  objective: string;
  content_category: string;
  campaign_type: string;
  mode: string;
  cpm: string;
  cap: string;
  post_cap: string;
  max_participants: string;
  submissions_per_creator: string;
  countries: string;
  languages: string;
  display_timezone: string;
  join_opens_at: string;
  join_closes_at: string;
  offer_expiry_hours: string;
  submission_deadline_at: string;
  campaign_ends_at: string;
  tracking_days: string;
  measurement_cutoff_at: string;
  min_live_days: string;
  earning_hold_days: string;
  source_assets: string;
  brief: Record<string, string>;
  /** Major-unit strings keyed by platform id. The pool for that platform. */
  platform_budgets: Record<string, string>;
  /** Major-unit CPM keyed by platform id. Price per 1,000 views. */
  platform_cpms: Record<string, string>;
}

export interface DraftVersionInput {
  summary: string;
  objective: string | null;
  content_category: string | null;
  campaign_type: string | null;
  brief: Record<string, string>;
  eligibility: Record<string, unknown>;
  mode: string;
  cpm_gross_minor: string;
  per_creator_cap_minor: string;
  per_post_cap_minor: string | null;
  max_participants: number | null;
  submissions_per_creator: number;
  display_timezone: string;
  join_opens_at: string | null;
  join_closes_at: string | null;
  offer_expiry_hours: number;
  submission_deadline_at: string | null;
  campaign_ends_at: string | null;
  tracking_days: number;
  measurement_cutoff_at: string | null;
  min_live_days: number;
  earning_hold_days: number;
  platform_terms?: PlatformTerm[] | null;
}

export interface DraftIssue {
  step: WizardStepId;
  field: string;
  message: string;
}

export interface DraftPayload {
  display_name: string;
  summary: string;
  objective: string | null;
  content_category: string | null;
  campaign_type: string | null;
  mode: string;
  cpm_gross_minor: number;
  per_creator_cap_minor: number;
  per_post_cap_minor: number | null;
  max_participants: number | null;
  submissions_per_creator: number;
  eligibility: { countries: string[]; languages: string[] };
  display_timezone: string;
  join_opens_at: string | null;
  join_closes_at: string | null;
  offer_expiry_hours: number;
  submission_deadline_at: string | null;
  campaign_ends_at: string | null;
  tracking_days: number;
  measurement_cutoff_at: string | null;
  min_live_days: number;
  earning_hold_days: number;
  measurement_routes: string[];
  brief: Record<string, string>;
  platform_terms: PlatformTerm[];
}

const asList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

const day = (iso: string | null | undefined) => iso?.slice(0, 10) ?? "";

const moneyFromMinor = (v: string | null | undefined) =>
  v == null || v === "" ? "" : (Number(v) / 100).toFixed(2);

export function parseCsvList(raw: string, caseMode: "upper" | "lower"): string[] {
  return raw.split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (caseMode === "upper" ? s.toUpperCase() : s.toLowerCase()));
}

/**
 * Whole major units plus up to two decimals, in cents.
 *
 * Accepts 0.1, .1, 0,1 and $0.1 so a per-creator cap typed the way a
 * person writes a tenth of a dollar still saves. Mid-keystroke values
 * ("4.") and zero still return null so autosave does not trip the CHECK.
 */
export function moneyToMinor(raw: string): number | null {
  let t = raw.trim().replace(/[$£€¥\u00a0\s]/g, "");
  if (!t) return null;
  if (t.includes(",") && !t.includes(".")) t = t.replace(",", ".");
  if (t.startsWith(".")) t = `0${t}`;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Math.round(Number(t) * 100);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function positiveInt(raw: string): number | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function nonNegativeInt(raw: string): number | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function dateToUtc(dayValue: string, endOfDay: boolean): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayValue)) return null;
  return endOfDay ? `${dayValue}T23:59:59Z` : `${dayValue}T00:00:00Z`;
}

/** New campaigns are inserted with $1 CPM / $10 cap. Treat that as blank until typed. */
function unsetCreateMoney(version: DraftVersionInput): boolean {
  return !version.summary.trim()
    && String(version.cpm_gross_minor) === "100"
    && String(version.per_creator_cap_minor) === "1000";
}

export function selectedPlatforms(form: Pick<DraftFormState, "brief">): string[] {
  return parseDraftPlatforms(form.brief["allowed_providers"] ?? "");
}

export function formFromVersion(displayName: string, version: DraftVersionInput): DraftFormState {
  const brief = { ...version.brief };
  const stub = unsetCreateMoney(version);
  const ids = parseDraftPlatforms(brief["allowed_providers"] ?? "");
  const capMinor = Number(version.per_creator_cap_minor);
  const legacyBudget = !stub && version.max_participants && Number.isInteger(capMinor) && capMinor > 0
    ? moneyFromMinor(String(version.max_participants * capMinor))
    : "";
  const legacyCpm = stub ? "" : moneyFromMinor(version.cpm_gross_minor);
  const saved = new Map((version.platform_terms ?? []).map((t) => [t.platform, t]));
  const platform_budgets: Record<string, string> = {};
  const platform_cpms: Record<string, string> = {};
  for (const id of ids) {
    const row = saved.get(id);
    platform_budgets[id] = row ? moneyFromMinor(String(row.budget_minor)) : legacyBudget;
    platform_cpms[id] = row ? moneyFromMinor(String(row.cpm_gross_minor)) : legacyCpm;
  }
  return {
    display_name: displayName,
    summary: version.summary,
    objective: version.objective ?? "",
    content_category: version.content_category ?? "",
    campaign_type: "AUTHORIZED_CLIPPING",
    mode: "OPEN_JOIN",
    cpm: legacyCpm,
    cap: stub ? "" : moneyFromMinor(version.per_creator_cap_minor),
    post_cap: moneyFromMinor(version.per_post_cap_minor),
    max_participants: "",
    submissions_per_creator: String(version.submissions_per_creator ?? 1),
    countries: "",
    languages: "",
    display_timezone: version.display_timezone || "UTC",
    join_opens_at: day(version.join_opens_at),
    join_closes_at: day(version.join_closes_at),
    offer_expiry_hours: String(version.offer_expiry_hours ?? 72),
    submission_deadline_at: day(version.submission_deadline_at),
    campaign_ends_at: day(version.campaign_ends_at ?? version.measurement_cutoff_at),
    tracking_days: String(version.tracking_days ?? 30),
    measurement_cutoff_at: day(version.measurement_cutoff_at),
    min_live_days: String(version.min_live_days ?? 7),
    earning_hold_days: String(version.earning_hold_days ?? 14),
    source_assets: brief["source_assets"] ?? "",
    brief,
    platform_budgets,
    platform_cpms,
  };
}

/**
 * Body for PATCH /campaigns/:id/draft.
 *
 * Returns null when a required money field is mid-edit so autosave does
 * not trip the CHECK (cpm_gross_minor > 0) on a half-typed rate.
 */
export function platformTerms(form: DraftFormState): PlatformTerm[] | null {
  const ids = selectedPlatforms(form);
  const terms: PlatformTerm[] = [];
  for (const id of ids) {
    const budget = moneyToMinor(form.platform_budgets[id] ?? "");
    const cpm = moneyToMinor(form.platform_cpms[id] ?? "");
    if (budget == null || cpm == null) return null;
    terms.push({ platform: id, budget_minor: budget, cpm_gross_minor: cpm });
  }
  return terms;
}

export function draftPayload(form: DraftFormState): DraftPayload | null {
  const cap = moneyToMinor(form.cap);
  const terms = platformTerms(form);
  if (cap == null || terms == null) return null;
  if (terms.some((t) => t.cpm_gross_minor > cap)) return null;
  const cpm = terms.length
    ? Math.min(...terms.map((t) => t.cpm_gross_minor))
    : moneyToMinor(form.cpm);
  if (cpm == null) return null;
  const post = form.post_cap.trim() === "" ? null : moneyToMinor(form.post_cap);
  if (form.post_cap.trim() !== "" && post == null) return null;
  if (post != null && post > cap) return null;

  const submissions = positiveInt(form.submissions_per_creator);
  const tracking = positiveInt(form.tracking_days);
  const minLive = nonNegativeInt(form.min_live_days);
  const hold = nonNegativeInt(form.earning_hold_days);
  if (submissions == null || tracking == null || minLive == null || hold == null) return null;

  const brief = {
    ...form.brief,
    source_assets: form.source_assets,
    allowed_providers: formatDraftPlatforms(selectedPlatforms(form)),
  };
  const platforms = selectedPlatforms(form);

  return {
    display_name: form.display_name.trim(),
    summary: form.summary,
    objective: null,
    content_category: null,
    campaign_type: "AUTHORIZED_CLIPPING",
    mode: "OPEN_JOIN",
    cpm_gross_minor: cpm,
    per_creator_cap_minor: cap,
    per_post_cap_minor: post,
    max_participants: null,
    submissions_per_creator: submissions,
    eligibility: { countries: [], languages: [] },
    display_timezone: form.display_timezone || "UTC",
    join_opens_at: form.join_opens_at ? dateToUtc(form.join_opens_at, false) : null,
    join_closes_at: form.submission_deadline_at
      ? dateToUtc(form.submission_deadline_at, true)
      : (form.join_closes_at ? dateToUtc(form.join_closes_at, true) : null),
    offer_expiry_hours: 72,
    submission_deadline_at: form.submission_deadline_at
      ? dateToUtc(form.submission_deadline_at, true) : null,
    campaign_ends_at: form.campaign_ends_at ? dateToUtc(form.campaign_ends_at, true) : null,
    tracking_days: tracking,
    measurement_cutoff_at: form.campaign_ends_at ? dateToUtc(form.campaign_ends_at, true) : null,
    min_live_days: minLive,
    earning_hold_days: hold,
    measurement_routes: platforms.length ? [...platforms, "sandbox"] : ["sandbox"],
    brief,
    platform_terms: terms,
  };
}

export function issuesFor(form: DraftFormState): DraftIssue[] {
  const issues: DraftIssue[] = [];
  const add = (step: WizardStepId, field: string, message: string) => {
    issues.push({ step, field, message });
  };

  if (form.display_name.trim().length < 2) {
    add("basics", "display_name", "Give the campaign a name.");
  }
  if (form.summary.trim().length < 8) {
    add("basics", "summary", "Write a short summary creators will see on the tile.");
  }

  for (const f of BRIEF_FIELDS) {
    if (f.kind === "platforms") {
      if (f.required && parseDraftPlatforms(form.brief[f.key] ?? "").length === 0) {
        add("brief", f.key, "Choose at least one platform.");
      }
      continue;
    }
    if (f.required && !(form.brief[f.key] ?? "").trim()) {
      add("brief", f.key, `Add ${f.label.toLowerCase()}.`);
    }
  }

  if (positiveInt(form.submissions_per_creator) == null) {
    add("creators", "submissions_per_creator", "At least one submission per creator.");
  }

  const cap = moneyToMinor(form.cap);
  if (cap == null) add("rewards", "cap", "Enter a per-creator cap greater than zero.");
  const ids = selectedPlatforms(form);
  for (const id of ids) {
    const budget = moneyToMinor(form.platform_budgets[id] ?? "");
    const rate = moneyToMinor(form.platform_cpms[id] ?? "");
    if (budget == null) {
      add("rewards", `budget:${id}`, "Enter a budget greater than zero for each platform.");
    }
    if (rate == null) {
      add("rewards", `cpm:${id}`, "Enter a price per 1,000 views for each platform.");
    } else if (cap != null && rate > cap) {
      add("rewards", `cpm:${id}`, "The price per 1,000 views cannot be higher than the per-creator cap.");
    }
  }
  const total = liveCampaignTotalMinor(form);
  if (ids.length > 0 && total != null && total < MIN_CAMPAIGN_BUDGET_MINOR) {
    add("rewards", "platform_budget", "The campaign budget must be at least $100.");
  }
  if (form.post_cap.trim()) {
    const post = moneyToMinor(form.post_cap);
    if (post == null) add("rewards", "post_cap", "Per-post cap must be a positive amount, or blank.");
    else if (cap != null && post > cap) {
      add("rewards", "post_cap", "A per-post cap cannot be higher than the per-creator cap.");
    }
  }

  if (!form.submission_deadline_at) {
    add("schedule", "submission_deadline_at", "Say the last day for content submission.");
  }
  if (!form.campaign_ends_at) {
    add("schedule", "campaign_ends_at", "Say when the campaign ends and review starts.");
  }
  if (form.join_opens_at && form.submission_deadline_at
      && form.submission_deadline_at <= form.join_opens_at) {
    add("schedule", "submission_deadline_at", "Content submission must be after the opening date.");
  }
  if (form.submission_deadline_at && form.campaign_ends_at
      && form.campaign_ends_at < form.submission_deadline_at) {
    add("schedule", "campaign_ends_at", "Campaign end must be on or after the submission date.");
  }
  if (form.join_opens_at && form.campaign_ends_at
      && form.campaign_ends_at <= form.join_opens_at) {
    add("schedule", "campaign_ends_at", "Campaign end must be after the opening date.");
  }
  if (positiveInt(form.tracking_days) == null) {
    add("schedule", "tracking_days", "Tracking days must be a whole number greater than zero.");
  }
  if (nonNegativeInt(form.min_live_days) == null) {
    add("schedule", "min_live_days", "Minimum live days must be zero or more.");
  }
  if (nonNegativeInt(form.earning_hold_days) == null) {
    add("schedule", "earning_hold_days", "Earning hold days must be zero or more.");
  }
  return issues;
}

export function issuesOn(form: DraftFormState, step: WizardStepId): DraftIssue[] {
  return issuesFor(form).filter((i) => i.step === step);
}

export function firstIncompleteStep(form: DraftFormState): WizardStepId {
  const issues = issuesFor(form);
  if (issues[0]) return issues[0].step;
  return "preview";
}

export function canSubmitDraft(form: DraftFormState): boolean {
  return issuesFor(form).length === 0 && draftPayload(form) != null;
}

/** Sum of the platform pools, in cents. Null until every selected platform has a budget. */
export function liveCampaignTotalMinor(form: DraftFormState): number | null {
  const ids = selectedPlatforms(form);
  if (ids.length === 0) return null;
  let total = 0;
  for (const id of ids) {
    const n = moneyToMinor(form.platform_budgets[id] ?? "");
    if (n == null) return null;
    total += n;
  }
  return Number.isSafeInteger(total) ? total : null;
}

export function liveFundingSplit(
  totalMinor: number, feeRate: number, taxRate: number,
): { netMinor: number; feeMinor: number; taxMinor: number; grossMinor: number } | null {
  if (!Number.isInteger(totalMinor) || totalMinor <= 0) return null;
  if (!(feeRate >= 0) || feeRate >= 1 || !(taxRate >= 0) || taxRate >= 1) return null;
  const feeMinor = Math.round(totalMinor * feeRate);
  const taxMinor = Math.round(totalMinor * taxRate);
  const grossMinor = totalMinor + feeMinor + taxMinor;
  if (!Number.isSafeInteger(grossMinor)) return null;
  return { netMinor: totalMinor, feeMinor, taxMinor, grossMinor };
}

/** Remaining to charge after settled funds and leftover transfers. */
export function liveRemainingMinor(
  totalMinor: number | null | undefined, fundedMinor: number | null | undefined,
): number | null {
  if (totalMinor == null || !Number.isFinite(totalMinor)) return null;
  const funded = Number.isFinite(fundedMinor) && (fundedMinor ?? 0) > 0 ? Number(fundedMinor) : 0;
  return Math.max(0, totalMinor - funded);
}
